/**
 * Veluna Ritual State - State Management Module
 * Deterministic bonus eligibility tracking for quiz experience
 * 
 * Usage:
 *   const eligibility = window.VelunaRitualState.getBonusEligibility();
 *   window.VelunaRitualState.setEmailCaptured('user@example.com');
 */

(function() {
  'use strict';

  window.VelunaRitualState = {
    /**
     * Get bonus eligibility state
     * @returns {Object} Eligibility object with emailCaptured, quizCompleted, isGuest, email, timestamp
     */
    getBonusEligibility: function() {
      try {
        return {
          emailCaptured: localStorage.getItem('veluna_quiz_email_captured') === 'true',
          quizCompleted: localStorage.getItem('veluna_quiz_completed') === 'true',
          isGuest: localStorage.getItem('veluna_quiz_guest') === 'true',
          email: localStorage.getItem('veluna_quiz_email') || null,
          timestamp: localStorage.getItem('veluna_quiz_email_timestamp') || null
        };
      } catch (e) {
        console.warn('[VelunaRitualState] Error reading eligibility:', e);
        return {
          emailCaptured: false,
          quizCompleted: false,
          isGuest: false,
          email: null,
          timestamp: null
        };
      }
    },
    
    
    /**
     * Set guest mode (user chose "Continue as Guest")
     * Guest mode allows viewing without bonus, but does NOT block email capture later
     */
    setGuestMode: function() {
      try {
        localStorage.setItem('veluna_quiz_guest', 'true');
        // NOTE: Do NOT clear email_captured - guest can unlock later by providing email
        
        if (localStorage.getItem('veluna_debug') === 'true' || window.VELUNA_DEBUG === true) {
          console.log('[VelunaRitualState] Guest mode set (email capture still allowed later)');
        }
      } catch (e) {
        console.error('[VelunaRitualState] Error setting guest mode:', e);
      }
    },
    
    /**
     * Legacy method name for backward compatibility
     * @deprecated Use setGuestMode() instead
     */
    setGuestPath: function() {
      this.setGuestMode();
    },
    
    /**
     * Set quiz completed flag
     */
    setQuizCompleted: function() {
      try {
        localStorage.setItem('veluna_quiz_completed', 'true');
        
        if (localStorage.getItem('veluna_debug') === 'true' || window.VELUNA_DEBUG === true) {
          console.log('[VelunaRitualState] Quiz completed flag set');
        }
      } catch (e) {
        console.error('[VelunaRitualState] Error setting quiz completed:', e);
      }
    },
    
    /**
     * Check if bonus is unlocked (email captured AND quiz completed AND NOT guest)
     * State Truth Table: BONUS_UNLOCKED = quiz_completed && email_captured && !guest
     * @returns {boolean} True if bonus is unlocked
     */
    isBonusUnlocked: function() {
      const eligibility = this.getBonusEligibility();
      return eligibility.quizCompleted && eligibility.emailCaptured && !eligibility.isGuest;
    },
    
    /**
     * Get bonus state (truth table)
     * @returns {string} 'BONUS_UNLOCKED' | 'BONUS_LOCKED_GUEST' | 'BONUS_LOCKED_NO_EMAIL' | 'IN_PROGRESS'
     */
    getBonusState: function() {
      const eligibility = this.getBonusEligibility();
      
      if (!eligibility.quizCompleted) {
        return 'IN_PROGRESS';
      }
      
      if (eligibility.isGuest) {
        return 'BONUS_LOCKED_GUEST';
      }
      
      if (eligibility.emailCaptured && eligibility.quizCompleted) {
        return 'BONUS_UNLOCKED';
      }
      
      return 'BONUS_LOCKED_NO_EMAIL';
    },
    
    /**
     * Set email captured (guest can unlock later by providing email)
     * Guest mode does NOT block email capture - users can unlock bonus later
     * @param {string} email - User's email address
     * @returns {boolean} True if email was set successfully
     */
    setEmailCaptured: function(email) {
      try {
        localStorage.setItem('veluna_quiz_email_captured', 'true');
        localStorage.setItem('veluna_quiz_email', email);
        localStorage.setItem('veluna_quiz_email_timestamp', Date.now().toString());
        localStorage.removeItem('veluna_quiz_guest'); // Clear guest flag when email provided
        
        if (localStorage.getItem('veluna_debug') === 'true' || window.VELUNA_DEBUG === true) {
          console.log('[VelunaRitualState] Email captured:', email);
        }
        return true;
      } catch (e) {
        console.error('[VelunaRitualState] Error setting email captured:', e);
        return false;
      }
    },
    
    /**
     * Clear all bonus-related state (for testing/debugging)
     */
    clearState: function() {
      try {
        localStorage.removeItem('veluna_quiz_email_captured');
        localStorage.removeItem('veluna_quiz_guest');
        localStorage.removeItem('veluna_quiz_completed');
        localStorage.removeItem('veluna_quiz_email_timestamp');
        // Note: We keep veluna_quiz_email for backward compatibility
      } catch (e) {
        console.error('[VelunaRitualState] Error clearing state:', e);
      }
    }
  };

  /**
   * Analytics event emitter (guarded - no errors if analytics not present)
   * @param {string} eventName - Event name
   * @param {Object} eventData - Event data
   */
  window.VelunaRitualState.emitEvent = function(eventName, eventData) {
    try {
      // Try dataLayer first (GTM)
      if (typeof window.dataLayer !== 'undefined' && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: eventName,
          ...eventData
        });
        return;
      }
      
      // Fallback to gtag (GA4)
      if (typeof gtag !== 'undefined') {
        gtag('event', eventName, eventData);
        return;
      }
      
      // No analytics available - silent no-op
      if (localStorage.getItem('veluna_debug') === 'true' || window.VELUNA_DEBUG === true) {
        console.log('[VelunaRitualState] Analytics not available, event not sent:', eventName, eventData);
      }
    } catch (e) {
      // Silently fail - don't break user experience
      if (localStorage.getItem('veluna_debug') === 'true' || window.VELUNA_DEBUG === true) {
        console.warn('[VelunaRitualState] Error emitting event:', e);
      }
    }
  };

  /**
   * Migrate legacy localStorage data
   * If legacy veluna_quiz_email exists and guest is not true, set veluna_quiz_email_captured=true
   */
  window.VelunaRitualState.migrateLegacyData = function() {
    try {
      const legacyEmail = localStorage.getItem('veluna_quiz_email');
      const isGuest = localStorage.getItem('veluna_quiz_guest') === 'true';
      const emailCaptured = localStorage.getItem('veluna_quiz_email_captured') === 'true';
      
      // If legacy email exists, guest is not true, and email_captured is not already set
      if (legacyEmail && !isGuest && !emailCaptured) {
        localStorage.setItem('veluna_quiz_email_captured', 'true');
        localStorage.setItem('veluna_quiz_email_timestamp', Date.now().toString());
        
        if (localStorage.getItem('veluna_debug') === 'true' || window.VELUNA_DEBUG === true) {
          console.log('[VelunaRitualState] Migrated legacy email data:', legacyEmail);
        }
        return true; // Migration occurred
      }
      return false; // No migration needed
    } catch (e) {
      console.warn('[VelunaRitualState] Error migrating legacy data:', e);
      return false;
    }
  };
  
  // Run migration on module load
  window.VelunaRitualState.migrateLegacyData();

  // Console log for verification (dev mode only)
  if (localStorage.getItem('veluna_debug') === 'true' || window.VELUNA_DEBUG === true) {
    console.log('[VelunaRitualState] Module loaded');
    console.log('[VelunaRitualState] Current eligibility:', window.VelunaRitualState.getBonusEligibility());
    console.log('[VelunaRitualState] Current bonus state:', window.VelunaRitualState.getBonusState());
  }
})();
